import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiCall } from '../lib/api';

interface OrderListItem {
  id: string;
  shopify_order_id: string;
  shopify_order_number: string;
  total: number;
  currency: string;
  financial_status: string | null;
  fulfillment_status: string | null;
  line_items?: Array<{ title: string; quantity: number }>;
  created_at: string;
  order_status_url?: string | null;
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  paid:      { label: 'ชำระแล้ว',  color: 'bg-green-100 text-green-700' },
  pending:   { label: 'รอชำระ',    color: 'bg-yellow-100 text-yellow-700' },
  refunded:  { label: 'คืนเงิน',  color: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'ยกเลิก',   color: 'bg-red-100 text-red-700' },
};

export function Orders() {
  const list = useQuery<{ orders: OrderListItem[] }>({
    queryKey: ['orders', 'me'],
    queryFn: () => apiCall<{ orders: OrderListItem[] }>('get', '/customers/me/orders'),
  });

  if (list.isLoading) return <div className="p-6 text-center text-gray-500">กำลังโหลด...</div>;

  const orders = list.data?.orders ?? [];

  return (
    <div className="max-w-md mx-auto px-4 py-6">
      <h1 className="text-xl font-bold mb-4">ประวัติคำสั่งซื้อ</h1>

      {orders.length === 0 && (
        <div className="card text-center py-12">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
               className="w-16 h-16 mx-auto mb-3 text-gray-300">
            <path strokeLinecap="round" strokeLinejoin="round"
                  d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"/>
          </svg>
          <div className="text-gray-500 mb-4">ยังไม่มีคำสั่งซื้อ</div>
          <Link to="/shop" className="btn btn-primary">เลือกสินค้าเลย</Link>
        </div>
      )}

      <div className="space-y-3">
        {orders.map((o) => {
          const status = STATUS_LABEL[o.financial_status ?? 'pending'] ?? STATUS_LABEL.pending!;
          const itemCount = o.line_items?.reduce((s, i) => s + i.quantity, 0) ?? 0;
          return (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              className="card block active:scale-[0.99] transition"
            >
              <div className="flex justify-between items-start mb-1">
                <div className="font-bold">{o.shopify_order_number}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                  {status.label}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {new Date(o.created_at).toLocaleDateString('th-TH', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
              </div>
              <div className="flex justify-between items-end mt-2">
                <div className="text-xs text-gray-500">{itemCount} รายการ</div>
                <div className="font-bold">
                  ฿{o.total.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
